import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, FileText, Inbox, LogOut, Package, Newspaper, FolderKanban, Library, Settings as SettingsIcon, ExternalLink, Layers, Factory, BookOpen, FileUp } from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import ContentManager from "@/pages/admin/ContentManager";
import DocumentManager from "@/pages/admin/DocumentManager";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "rfqs", label: "RFQ Inquiries", icon: Inbox },
  { id: "messages", label: "Messages", icon: FileText },
  { id: "products", label: "Products", icon: Package },
  { id: "categories", label: "Categories", icon: Layers },
  { id: "industries", label: "Industries", icon: Factory },
  { id: "posts", label: "Insights", icon: Newspaper },
  { id: "knowledge", label: "Knowledge", icon: BookOpen },
  { id: "documents", label: "Documents", icon: FileUp },
  { id: "glossary", label: "Glossary", icon: Library },
  { id: "projects", label: "Projects", icon: FolderKanban },
  { id: "resources", label: "Resources", icon: Library },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const RFQ_STATUSES = ["new", "in_review", "quoted", "archived"];

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <p className="text-sm text-slate-400">Loading…</p>;
  const cards = [
    ["RFQ Inquiries", stats.rfqs, `${stats.rfqs_new} new`],
    ["Contact Messages", stats.messages, ""],
    ["Products", stats.products, "published catalogue"],
    ["Articles", stats.posts, "insights"],
    ["Projects", stats.projects, "applications"],
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="admin-overview">
      {cards.map(([label, value, sub]) => (
        <div key={label} className="border border-line bg-white p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">{label}</p>
          <p className="mt-3 font-display text-4xl font-medium text-ink">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
        </div>
      ))}
    </div>
  );
}

function RFQs() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => { api.get("/admin/rfqs").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/rfqs/${id}`, { status });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      load();
    } catch { toast.error("Could not update status"); }
  };

  if (!items.length) return <p className="border border-line bg-white p-8 text-sm text-steel" data-testid="rfq-empty">No RFQ inquiries yet. Submissions from the Request a Quote form appear here.</p>;

  return (
    <div className="space-y-3" data-testid="admin-rfq-list">
      {items.map((r) => (
        <div key={r.id} className="border border-line bg-white">
          <button data-testid={`rfq-row-${r.id}`} onClick={() => setOpenId(openId === r.id ? null : r.id)}
            className="flex w-full flex-wrap items-center justify-between gap-3 p-5 text-left">
            <div>
              <p className="font-medium text-ink">{r.name} {r.company && <span className="text-steel">· {r.company}</span>}</p>
              <p className="mt-0.5 text-xs text-slate-400">{r.product || "General enquiry"} · {r.country || "—"} · {new Date(r.created_at).toLocaleString()}</p>
            </div>
            <span className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${r.status === "new" ? "bg-accent-light text-accent" : "bg-mist text-steel"}`}>
              {r.status.replace("_", " ")}
            </span>
          </button>
          {openId === r.id && (
            <div className="border-t border-line p-5 text-sm" data-testid={`rfq-detail-${r.id}`}>
              <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                {[["Email", r.email], ["Phone", r.phone], ["City / Country", `${r.city || "—"} / ${r.country || "—"}`],
                  ["Product", r.product], ["Quantity", r.quantity], ["Dimensions", r.dimensions],
                  ["Material", r.material], ["Application", r.application], ["Timeline", r.timeline]]
                  .filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}><dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-steel">{k}</dt><dd className="mt-0.5 text-ink">{v}</dd></div>
                  ))}
              </dl>
              {r.project_details && <p className="mt-4 border-l-2 border-accent pl-4 text-steel">{r.project_details}</p>}
              {r.message && <p className="mt-2 border-l-2 border-line pl-4 text-steel">{r.message}</p>}
              {r.attachment && (
                <a href={`/api/uploads/${r.attachment}`} target="_blank" rel="noopener noreferrer"
                  data-testid={`rfq-attachment-${r.id}`} className="mt-4 inline-flex items-center gap-1.5 text-accent hover:underline">
                  <ExternalLink size={13} /> View attachment
                </a>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                {RFQ_STATUSES.filter((s) => s !== r.status).map((s) => (
                  <button key={s} data-testid={`rfq-status-${s}-${r.id}`} onClick={() => setStatus(r.id, s)}
                    className="border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel transition-colors hover:border-accent hover:text-accent">
                    Mark {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Messages() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get("/admin/messages").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try { await api.patch(`/admin/messages/${id}`, { status: "read" }); load(); } catch { /* noop */ }
  };

  if (!items.length) return <p className="border border-line bg-white p-8 text-sm text-steel" data-testid="messages-empty">No contact messages yet.</p>;

  return (
    <div className="space-y-3" data-testid="admin-messages-list">
      {items.map((m) => (
        <div key={m.id} className="border border-line bg-white p-5" data-testid={`message-${m.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-medium text-ink">{m.name} <span className="text-steel">· {m.email}</span></p>
            <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</span>
          </div>
          {m.subject && <p className="mt-1 text-sm font-medium text-ink">{m.subject}</p>}
          <p className="mt-2 text-sm leading-relaxed text-steel">{m.message}</p>
          {m.status === "new" && (
            <button data-testid={`message-read-${m.id}`} onClick={() => markRead(m.id)}
              className="mt-3 border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel hover:border-accent hover:text-accent">
              Mark read
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/site").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    const body = { ...form };
    if (typeof body.phones === "string") {
      body.phones = body.phones.split(",").map((s) => s.trim()).filter(Boolean);
    }
    try { await api.put("/admin/settings/site", body); toast.success("Settings saved"); }
    catch { toast.error("Could not save settings"); }
    finally { setSaving(false); }
  };

  const fields = [
    ["email", "Business Email"], ["phone", "Primary Phone"], ["phones", "All Phone Numbers (comma separated)"],
    ["whatsapp", "WhatsApp Number"], ["address", "Address"], ["maps_url", "Google Maps Link"],
    ["note", "Internal Note"],
  ];
  const imageFields = [
    ["img_home_hero", "Homepage Hero Image URL"],
    ["img_about_primary", "About Page — Primary Image URL"],
    ["img_about_secondary", "About Page — Secondary Image URL"],
    ["img_manufacturing_primary", "Home Manufacturing — Large Image URL"],
    ["img_manufacturing_secondary", "Home Manufacturing — Small Image URL"],
    ["img_quality", "Home Quality Section Image URL"],
  ];
  return (
    <div className="max-w-2xl space-y-8" data-testid="admin-settings">
      <div className="border border-line bg-white p-7">
        <p className="text-sm text-steel">Official contact details shown across the website — phone, email, address, WhatsApp and the Google Maps directions link.</p>
        <div className="mt-6 space-y-5">
          {fields.map(([k, label]) => (
            <div key={k}>
              <label htmlFor={`set-${k}`} className="label-field">{label}</label>
              <input id={`set-${k}`} data-testid={`settings-${k}-input`} className="input-field"
                value={Array.isArray(form[k]) ? form[k].join(", ") : form[k] || ""} onChange={set(k)} />
            </div>
          ))}
        </div>
      </div>
      <div className="border border-line bg-white p-7">
        <h3 className="font-display text-lg font-semibold text-ink">Site Images</h3>
        <p className="mt-2 text-sm text-steel">
          Replace key page images here when actual STJ product and factory photographs are ready. Leave blank to keep the current premium placeholder. Product, category, project and article images are edited in their own tabs.
        </p>
        <div className="mt-6 space-y-5">
          {imageFields.map(([k, label]) => (
            <div key={k}>
              <label htmlFor={`set-${k}`} className="label-field">{label}</label>
              <input id={`set-${k}`} data-testid={`settings-${k}-input`} className="input-field" placeholder="https://… (blank = current placeholder)" value={form[k] || ""} onChange={set(k)} />
            </div>
          ))}
        </div>
      </div>
      <button onClick={save} data-testid="settings-save-button" disabled={saving}
        className="bg-accent px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark disabled:opacity-60">
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
}

export default function AdminDashboard() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("overview");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => { if (user === null) navigate("/admin/login"); }, [user, navigate]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    window.localStorage.removeItem("stj_admin_token");
    navigate("/admin/login");
  };

  if (!user) return <div className="flex min-h-screen items-center justify-center bg-mist"><p className="text-sm text-steel">Loading…</p></div>;

  return (
    <div className="min-h-screen bg-mist" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-line bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="admin-logo-link"><Logo /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-[11px] uppercase tracking-[0.18em] text-steel sm:block">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 border border-line px-3.5 py-2 text-xs font-semibold text-ink transition-colors hover:border-accent hover:text-accent">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex gap-1 overflow-x-auto border-b border-line pb-px" role="tablist" aria-label="Admin sections">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`admin-tab-${id}`} role="tab" aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-[13px] font-medium transition-colors ${tab === id ? "border-accent text-accent" : "border-transparent text-steel hover:text-ink"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        <div className="mt-8">
          {tab === "overview" && <Overview />}
          {tab === "rfqs" && <RFQs />}
          {tab === "messages" && <Messages />}
          {tab === "products" && <ContentManager kind="products" title="Products" />}
          {tab === "categories" && <ContentManager kind="categories" title="Product Categories" />}
          {tab === "industries" && <ContentManager kind="industries" title="Industries" />}
          {tab === "posts" && <ContentManager kind="posts" title="Insights / Blog" />}
          {tab === "knowledge" && <ContentManager kind="knowledge" title="Knowledge Center Articles" />}
          {tab === "documents" && <DocumentManager />}
          {tab === "glossary" && <ContentManager kind="glossary" title="Glossary Terms" />}
          {tab === "projects" && <ContentManager kind="projects" title="Projects" />}
          {tab === "resources" && <ContentManager kind="resources" title="Resources" />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}
